import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FileText, CheckCircle } from '@phosphor-icons/react';
import HaleConsoleLayout from '../../components/hale/HaleConsoleLayout';
import { T, FONT, PageHeading, ConsoleCard, Eyebrow, StatusPill, InfoRow, EmptyState, Spinner } from './_theme';

const API = process.env.REACT_APP_BACKEND_URL;
const TRACK_LABEL = { hale_reviewed: 'HALE Reviewed™', hale_verified: 'HALE Verified™', both: 'Reviewed™ + Verified™' };

export default function HaleApplicationsPage() {
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [none, setNone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/hale-marketplace/applications/me`, { withCredentials: true });
        setApp(r.data);
      } catch (e) {
        if (e?.response?.status === 401) { navigate('/hale/login', { replace: true }); return; }
        if (e?.response?.status === 404) setNone(true);
      } finally { setLoading(false); }
    })();
  }, [navigate]);

  const brand = app?.draft?.brand || {};
  const rec = app?.recommendation || {};
  const decision = app?.decision || null;

  return (
    <HaleConsoleLayout>
      <PageHeading title="Applications" sub="Track the status of your Hale League application and review outcome." testid="hale-applications-title" />

      {loading && <Spinner label="Loading your application…" />}

      {!loading && none && (
        <EmptyState icon={FileText} title="No application on file yet" testid="hale-applications-empty"
          desc="Start your Hale League application — it takes about 6 minutes and is auto-saved as you go."
          ctaLabel="Begin application" ctaTo="/hale/apply" />
      )}

      {!loading && app && (
        <div className="space-y-5">
          <ConsoleCard data-testid="hale-applications-card">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <Eyebrow>Application</Eyebrow>
                <p className="text-[20px] font-bold tracking-tight" style={{ fontFamily: FONT.DISPLAY, color: T.INK }}>
                  {brand.brand_name || app?.draft?.founder?.full_name || 'Your application'}
                </p>
              </div>
              <StatusPill status={app.status} testid="hale-applications-status" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
              <InfoRow label="Review pathway" value={TRACK_LABEL[rec.track] || 'Determined after review'} />
              <InfoRow label="Submitted" value={app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : 'Draft'} />
              <InfoRow label="Website" value={brand.website} />
              <InfoRow label="Country" value={brand.country} />
            </div>
          </ConsoleCard>

          {decision && (
            <ConsoleCard data-testid="hale-applications-decision">
              <Eyebrow>Review outcome</Eyebrow>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle size={18} weight="fill" style={{ color: T.RED }} />
                <StatusPill status={app.status} />
                {decision.decided_at && <span className="text-[12px]" style={{ color: T.MUTED }}>· {new Date(decision.decided_at).toLocaleDateString()}</span>}
              </div>
              {decision.reviewer_notes && <p className="text-[14px] leading-relaxed" style={{ color: T.SOFT }}>{decision.reviewer_notes}</p>}
              {Array.isArray(decision.recommendations) && decision.recommendations.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {decision.recommendations.map((r, i) => (
                    <li key={i} className="text-[13px] flex gap-2" style={{ color: T.SOFT }}>
                      <span style={{ color: T.RED }}>•</span> {typeof r === 'string' ? r : r.text || JSON.stringify(r)}
                    </li>
                  ))}
                </ul>
              )}
            </ConsoleCard>
          )}
        </div>
      )}
    </HaleConsoleLayout>
  );
}
